import { useCallback, useMemo } from "react";
import { ArrowRight } from "lucide-react";
import { useNumericCell } from "../../hooks/useNumericCell";
import { useCellNavigation } from "../../utils/cellNavigation";
import { formatDecimal, roundDecimal } from "../../utils/decimalUtils";
import { getJunctionWeight } from "../../utils/resolveProductDefaults";
import { useProductLookups } from "../../contexts/ProductLookupsContext";
import type { QuantityCellProps } from "./types";
import { CELL_INPUT } from "./styles";
import { cn } from "@/lib/utils";
import {
  AI_PENDING_CELL_CLASS,
  getAiChangeValues,
  isAiPendingField,
} from "./AiChangeBadge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@stockline/ui/components/tooltip";

export function QuantityCell({
  flow,
  availableUnits = [],
  disabled,
  onFlowUpdate,
  onWeightPreview,
  proposalDiff,
}: QuantityCellProps) {
  // Diff metadata — two sources are unified into a single `{ proposedNum }`:
  //   1. Legacy `proposalDiff.fieldDiffs.quantity.newValue` (string).
  //   2. `flow_metadata.ai_suggestion.changes.quantity.to` (number).
  // The ai_suggestion source wins when both exist; the server clears the
  // block after the PO-level approve/decline, at which point the cell
  // collapses back to the raw flow value.
  const aiChange = getAiChangeValues(flow, "quantity");
  const aiPending = isAiPendingField(flow, "quantity");
  // Explicit check on `aiChange` rather than `aiChange?.to ?? ...`: `to` can
  // be `null` (AI legitimately proposes clearing the quantity), which `??`
  // would treat as absent and fall through to the legacy proposalDiff.
  const proposedRaw = aiChange
    ? aiChange.to
    : proposalDiff?.fieldDiffs.quantity?.newValue;
  const proposedNum =
    proposedRaw != null && proposedRaw !== "" ? Number(proposedRaw) : null;
  const effectiveQuantity =
    proposedNum != null && Number.isFinite(proposedNum)
      ? proposedNum
      : (flow.quantity ?? null);
  const showOldStrike =
    (aiPending || proposalDiff != null) &&
    proposedNum != null &&
    Number.isFinite(proposedNum) &&
    flow.quantity != null &&
    Number(flow.quantity) !== proposedNum;
  // Dexie-backed flows arrive without nested `product`/`unit` (see
  // `mapOfflineFlowToLegacy`); only flows freshly added via the modify
  // buffer carry them inline. Resolve from the FlowsTable-level lookups
  // so existing rows get the same junction-driven weight preview + auto-
  // weight-on-blur as newly-added rows. Guard against the inline object
  // going stale w.r.t. its id (e.g. a unit change committed via
  // `onFlowUpdate({ unit_id })` without a matching inline replacement) —
  // mirrors UnitCell.tsx:126-131.
  const lookups = useProductLookups();
  const resolvedProduct = useMemo(() => {
    if (flow.product?.id === flow.product_id) return flow.product;
    if (!flow.product_id) return null;
    return lookups?.productMap.get(flow.product_id) ?? null;
  }, [flow.product, flow.product_id, lookups?.productMap]);
  const resolvedUnit = useMemo(() => {
    if (flow.unit?.id === flow.unit_id) return flow.unit;
    if (!flow.unit_id) return null;
    return availableUnits.find((u) => u.id === flow.unit_id) ?? null;
  }, [flow.unit, flow.unit_id, availableUnits]);

  // Resolve junction weight once (skip per_box: qty doesn't scale weight there)
  const junctionWeight = useMemo(() => {
    if (resolvedUnit?.per_box) return null;
    return getJunctionWeight(resolvedProduct, resolvedUnit);
  }, [resolvedProduct, resolvedUnit]);

  // On blur: persist quantity + computed weight via API
  const onQuantityUpdate = useCallback(
    (flowId: string, updates: Record<string, unknown>) => {
      const newQty = updates.quantity as number | null;
      if (newQty != null && junctionWeight != null) {
        onFlowUpdate(flowId, { ...updates, weight: newQty * junctionWeight });
        return;
      }
      onFlowUpdate(flowId, updates);
    },
    [onFlowUpdate, junctionWeight],
  );

  // On each keystroke: instant local weight preview (no API call).
  // Mirrors persistence: handleBlur coerces parsed ?? 0, so null/empty input
  // commits weight=0 — preview must show the same to avoid a snap on blur.
  const onParsedChange = useCallback(
    (parsed: number | null) => {
      if (junctionWeight == null) {
        onWeightPreview?.(null);
        return;
      }
      const qty = parsed ?? 0;
      onWeightPreview?.(roundDecimal(qty * junctionWeight));
    },
    [junctionWeight, onWeightPreview],
  );

  const { localValue, handleChange, handleBlur } = useNumericCell(
    effectiveQuantity,
    flow.id,
    "quantity",
    onQuantityUpdate,
    onParsedChange,
  );

  const blurTarget = useCallback(
    (target: HTMLInputElement) => target.blur(),
    [],
  );
  const handleKeyDown = useCellNavigation({ onBeforeNavigate: blurTarget });

  const input = (
    <input
      type="text"
      inputMode="decimal"
      data-testid="flow-quantity-input"
      data-tab-cell
      value={localValue}
      onChange={(e) => handleChange(e.target.value)}
      onFocus={(e) => e.target.select()}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder="0"
      disabled={disabled}
      className={cn(CELL_INPUT, showOldStrike && "text-violet-700 font-medium")}
    />
  );

  // Wrap the input in a flex span so the AI-pending tint is reachable even
  // when there is no strike to render (e.g. AI suggests the same value or a
  // non-modify suggestion type). Without the wrapper the cell loses its violet
  // chrome and the user can't tell a pending suggestion exists on the field.
  if (!showOldStrike) {
    if (!aiPending) return input;
    return (
      <span className={cn("flex items-center min-w-0", AI_PENDING_CELL_CLASS)}>
        {input}
      </span>
    );
  }

  const oldFormatted = formatDecimal(flow.quantity);
  const newFormatted = formatDecimal(proposedNum);

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "flex items-center gap-1 min-w-0 cursor-help",
              aiPending && AI_PENDING_CELL_CLASS,
            )}
          >
            <span className="text-xs text-muted-foreground line-through truncate max-w-[3rem] shrink-0">
              {oldFormatted}
            </span>
            <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
            {input}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <span className="line-through text-muted-foreground">
            {oldFormatted}
          </span>
          <span className="mx-1">→</span>
          <span className="font-medium text-violet-700">{newFormatted}</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
